import React, { useEffect, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { AppStore } from "../../../../redux/store";
import { MAP_CONFIG_BY_COUNTRY, MAP_OPTIONS } from "./mapConfig";
import { buildPolygons, clearPolygons } from "./polygonRenderer";
import { GadmFeature } from "./types";

interface Props {
  features: GadmFeature[];
  loading?: boolean;
  onFeatureClick: (feature: GadmFeature) => void;
}

const Map: React.FC<Props> = ({ features, loading, onFeatureClick }) => {
  const country = useSelector((store: AppStore) => get(store, "country.country", "sv"));
  const mapRef = useRef<any>();
  const [isLoaded, setIsLoaded] = useState(false);
  const drawnPolygonsRef = useRef<google.maps.Polygon[]>([]);

  const config = MAP_CONFIG_BY_COUNTRY[country] ?? MAP_CONFIG_BY_COUNTRY.sv;

  // Suprime el error de GTM que interfiere con la carga de tiles de Google Maps.
  // El mapa sigue funcionando — solo es ruido del overlay de desarrollo.
  useEffect(() => {
    const handler = (event: ErrorEvent) => {
      if (event.message?.includes("insertBefore") || event.message?.includes("Unexpected token '<'")) {
        event.preventDefault();
      }
    };
    // capture: true para interceptar antes que el overlay de desarrollo de React
    window.addEventListener("error", handler, true);
    return () => window.removeEventListener("error", handler, true);
  }, []);

  // Centra el mapa en el país usando setCenter + setZoom en lugar de fitBounds
  // para evitar conflicto con GTM durante animaciones grandes (ej. Panamá)
  const focusCountry = (map: any, countryKey: string) => {
    const cfg = MAP_CONFIG_BY_COUNTRY[countryKey] ?? MAP_CONFIG_BY_COUNTRY.sv;
    map.setCenter(cfg.center);
    map.setZoom(cfg.zoom);
  };

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    const timer = setTimeout(() => focusCountry(mapRef.current, country), 50);
    return () => clearTimeout(timer);
  }, [country, isLoaded]);

  // Fuerza cursor "wait" sobre todo el mapa inyectando una regla CSS global con !important
  useEffect(() => {
    if (!loading) return;
    const style = document.createElement("style");
    style.textContent = "* { cursor: wait !important; }";
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, [loading]);

  // Redibuja polígonos y enfoca la vista cuando cambia la lista de features
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    clearPolygons(drawnPolygonsRef.current);
    drawnPolygonsRef.current = buildPolygons({ map: mapRef.current, features, onFeatureClick });

    if (features.length === 0) return;

    // Siempre enfocar todos los polígonos cargados (zoom-in al entrar, zoom-out al salir)
    const bounds = new window.google.maps.LatLngBounds();
    features.forEach(f => {
      const rings = f.geometry.type === "MultiPolygon"
        ? (f.geometry.coordinates as number[][][][]).flatMap(p => p)
        : f.geometry.coordinates as number[][][];
      rings.forEach(ring => ring.forEach(([lng, lat]) => bounds.extend({ lat, lng })));
    });
    mapRef.current.fitBounds(bounds, 40);
  }, [features, isLoaded]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_MAPS_KEY ?? "" }}
        defaultCenter={config.center}
        defaultZoom={config.zoom}
        options={MAP_OPTIONS}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map;
          setIsLoaded(true);
          focusCountry(map, country);
          // Desactiva eventos en el overlay transparente que google-map-react crea para children de React.
          // No usamos ese overlay (los polígonos se montan directo en la API de Google Maps),
          // así que solo interfiere capturando clicks destinados a los polígonos.
          const container = map.getDiv()?.parentElement;
          const overlay = container?.querySelector('[style*="background-color: transparent"]') as HTMLElement | null;
          if (overlay) overlay.style.pointerEvents = "none";
        }}
      />
    </div>
  );
};

export default Map;
